import {
  AlertColor,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { FC, MouseEvent, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import strings from '../../../../common/Translation/Translate';
import Notification from '../../../../components/Notification';
import { ToolTip } from '../../../../components/Tooltip/ToolTip';
import axiosInstance from '../../../../utils/axios';
import { privileges } from '../../../../utils/constants/privileges';
import { routes } from '../../../../utils/constants/routes';
import { URLS } from '../../../../utils/constants/urls';
import { Privilege } from '../../../../utils/redux/reducer/authentication-slice';
import Delete from '../../CommonPage/Delete';
import { formatLastUpdatedCell } from '../../CommonPage/lastUpdatedColumn';
import { VenueConfigurationHealthCell } from '../VenueConfiguration/VenueConfigurationHealthPanel';

const MAX_VISIBLE_ROWS = 5;

type VenueConfigRow = {
  id: number;
  eventTypeName?: string;
  available?: boolean;
  completionPercentage?: number;
  configurationHealth?: { label: string; complete: boolean }[];
  missingFields?: string[];
  editedAt?: string | null;
  editedBy?: string | null;
};

type VenueConfigsExpandPanelProps = {
  venueId: number;
  venueName?: string;
};

const stopRowAction = (event: MouseEvent) => {
  event.stopPropagation();
};

export const VenueConfigsExpandPanel: FC<VenueConfigsExpandPanelProps> = ({ venueId, venueName }) => {
  const navigate = useNavigate();
  const userPrivileges = useSelector(Privilege) as string[];
  const canEdit = userPrivileges?.includes(privileges.edit_venue_configuration);
  const canAdd = userPrivileges?.includes(privileges.add_venue_configuration);
  const canDelete = userPrivileges?.includes(privileges.delete_venue_configuration);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<VenueConfigRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState('');
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  const loadConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.post(URLS.venueConfiguration, {
        page: 1,
        size: MAX_VISIBLE_ROWS,
        filter: [`venueId:eq:${venueId}`],
        sort: ['createdAt:desc'],
      });
      setRows(Array.isArray(data?.content) ? data.content : []);
      setTotalCount(typeof data?.totalElements === 'number' ? data.totalElements : 0);
    } catch {
      setRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [venueId]);

  useEffect(() => {
    void loadConfigs();
  }, [loadConfigs]);

  const addConfiguration = () => {
    navigate(`${routes.bulkCreateVenueConfiguration}?venueId=${venueId}`);
  };

  const openVenueConfigList = () => {
    navigate(routes.venueConfiguration);
  };

  const editConfiguration = (configId: number) => {
    navigate(`${routes.editVenueConfiguration}/${configId}`);
  };

  const cloneConfiguration = async (configId: number) => {
    try {
      const { status, data } = await axiosInstance.get(URLS.venueConfigCopy(configId));
      if (status === 200) {
        setMessage({
          display: true,
          severity: 'success',
          message: strings.copied_successfully_text,
        });
        setTimeout(() => {
          navigate(`${routes.editVenueConfiguration}/${data.id}?type=copy`);
        }, 1000);
      }
    } catch (error: any) {
      setMessage({
        display: true,
        severity: 'error',
        message: error.response?.data?.message || error.message,
      });
    }
  };

  const openDeleteModal = (configId: number) => {
    setDeleteId(String(configId));
    setDeleteOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteOpen(false);
    setDeleteId('');
  };

  const hiddenCount = Math.max(0, totalCount - MAX_VISIBLE_ROWS);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 1.25, gap: 1 }}>
        <CircularProgress size={18} />
        <Typography sx={{ fontSize: 13, color: '#667085' }}>Loading configurations…</Typography>
      </Box>
    );
  }

  if (totalCount === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          py: 1,
          px: 2,
          bgcolor: '#F9FAFB',
        }}>
        <Typography sx={{ fontSize: 13, color: '#667085' }}>
          No configurations for {venueName || 'this venue'} yet.
        </Typography>
        {canAdd ? (
          <Button size="small" variant="contained" onClick={addConfiguration} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Add configuration
          </Button>
        ) : null}
      </Box>
    );
  }

  return (
    <Box>
      {message.display ? (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={value => setMessage({ ...message, display: value })}
        />
      ) : null}
      <Delete
        openModal={deleteOpen}
        closeModal={closeDeleteModal}
        deleteUrl={URLS.get_edit_delete_VenueConfigurationById(Number(deleteId))}
        updateList={loadConfigs}
      />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 0.75,
          gap: 1,
          borderBottom: '1px solid #EAECF0',
          bgcolor: '#F9FAFB',
          minHeight: 40,
        }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#344054' }}>
          Configurations ({totalCount})
        </Typography>
        {canAdd ? (
          <Button size="small" variant="outlined" onClick={addConfiguration} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Add configuration
          </Button>
        ) : null}
      </Box>

      <Box sx={{ overflow: 'hidden', bgcolor: '#fff' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#F9FAFB' }}>
              {['Segment', 'Status', 'Health', 'Last Updated', 'Actions'].map((header, index) => (
                <TableCell
                  key={header}
                  align={index === 2 || index === 4 ? 'center' : 'left'}
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#667085',
                    py: 1,
                    borderBottom: '1px solid #EAECF0',
                    width: index === 4 ? 120 : undefined,
                  }}>
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.id} hover>
                <TableCell sx={{ py: 1.25 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#101828' }}>
                    {row.eventTypeName || '—'}
                  </Typography>
                </TableCell>
                <TableCell sx={{ py: 1.25 }}>
                  <Chip
                    size="small"
                    label={row.available ? strings.venue_column_available || 'Available' : strings.venue_column_not_available || 'Not Available'}
                    sx={{
                      height: 22,
                      fontSize: 11,
                      fontWeight: 700,
                      bgcolor: row.available ? '#ECFDF3' : '#F2F4F7',
                      color: row.available ? '#027A48' : '#344054',
                    }}
                  />
                </TableCell>
                <TableCell align="center" sx={{ py: 1.25 }}>
                  <VenueConfigurationHealthCell
                    value={Number(row.completionPercentage ?? 0)}
                    configurationHealth={row.configurationHealth}
                    missingFields={row.missingFields}
                  />
                </TableCell>
                <TableCell sx={{ py: 1.25 }}>{formatLastUpdatedCell(row.editedAt, row.editedBy)}</TableCell>
                <TableCell align="center" sx={{ py: 1.25 }} onClick={stopRowAction}>
                  <Box sx={{ display: 'inline-flex', gap: 0.25 }}>
                    {canEdit ? (
                      <ToolTip title="Edit">
                        <IconButton size="small" onClick={() => editConfiguration(row.id)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </ToolTip>
                    ) : null}
                    {canAdd ? (
                      <ToolTip title={strings.copy}>
                        <IconButton size="small" onClick={() => cloneConfiguration(row.id)}>
                          <ContentCopyOutlinedIcon fontSize="small" />
                        </IconButton>
                      </ToolTip>
                    ) : null}
                    {canDelete ? (
                      <ToolTip title={strings.deleteText}>
                        <IconButton size="small" onClick={() => openDeleteModal(row.id)}>
                          <DeleteOutlineOutlinedIcon fontSize="small" color="error" />
                        </IconButton>
                      </ToolTip>
                    ) : null}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {hiddenCount > 0 ? (
        <Button
          size="small"
          onClick={openVenueConfigList}
          sx={{
            display: 'block',
            width: '100%',
            borderRadius: 0,
            py: 1,
            textTransform: 'none',
            fontWeight: 700,
            color: '#175CD3',
            borderTop: '1px solid #EAECF0',
            bgcolor: '#F9FAFB',
          }}>
          +{hiddenCount} more — view all configurations
        </Button>
      ) : null}
    </Box>
  );
};

export default VenueConfigsExpandPanel;
